import {
    HistoryRounded as HistoryRoundedIcon,
    MoneyOffRounded as MoneyOffRoundedIcon,
    MoreVertOutlined as MoreVertOutlinedIcon,
    PlayCircle as PlayCircleIcon,
} from 'components/icons';
import Chip from 'components/@extended/Chip.tsx';
import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import { CallForActionButtonTooltip } from 'components/buttons/CallForActionButton';
import DisplayHistoryButton from 'components/buttons/DisplayHistoryButton';
import PermissionButton from 'components/buttons/PermissionButton';
import DmsLink from 'components/dmslink/DmsLink.tsx';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import type { DraftActions } from 'components/drafts/useDraftActions';
import EmployeeName from 'components/employee/EmployeeName';
import MatterIndicators from 'components/matters/MatterIndicators';
import Loader from 'components/Loader';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import KebabMenu, { MenuItem } from 'components/navigation/KebabMenu';
import PageHeader from 'components/PageHeader';
import PartyName from 'components/party/PartyName';
import { Tooltip } from 'components/ui/tooltip';
import CopyToClipboardIcon from 'components/util/CopyToClipboard';
import useAuth from 'hooks/useAuth';
import { useAddAndStartTimecard } from 'pages/timecards/useAddAndStartTimecard';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useGetInvoicesWithFiltersQuery } from 'store/api/invoices';
import { useDeleteMatterMutation, useLazyGetMatterDeleteValidationQuery } from 'store/api/matters';
import { useGetPaginatedTimecardsWithFiltersQuery } from 'store/api/timecards';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features.ts';
import { HistoryEntityType, HistoryFieldType } from 'types/histories';
import { MatterAddPrefill, MatterDeleteValidation, MatterDto, MatterStateType } from 'types/matters';
import { errorAlert, warningAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { NamedEntityParts } from 'utils/nameOrder';
import { PartyNameParty } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import Blocked from './Blocked.tsx';
import EditableMatterCaseNumber from './components/EditableMatterCaseNumber';
import { buildDeleteBlockerMessage } from './matterDeleteValidationMessage';
import OverdueInvoices from './OverdueInvoices';
import { useCloseCaseMenuItem } from './closing/CloseCaseMenuItem';
import { useReopenAsOfferPending } from './useReopenAsOfferPending';
import { getStatusChipType } from './util';

interface Props {
    matter: MatterDto;
    classifiers: Record<string, ClassifierData>;
    onOpenHistory: () => void;
}

const SECTION_CLASS = 'flex items-center gap-[5px] text-[12px] leading-5 font-medium';
const HEADER_LINK_CLASS = 'text-link w-fit no-underline hover:underline';

export const MatterPageHeader = ({ matter, classifiers, onOpenHistory }: Props): JSX.Element => {
    const { openModal, closeModal } = useModal();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/matters');
    const leaveDeleted = useLeaveDeletedEntity('/matters');
    const [deleteMatter, { isLoading: deleteMatterLoading }] = useDeleteMatterMutation();
    const [fetchDeleteValidation] = useLazyGetMatterDeleteValidationQuery();
    const canCreateTimecardsOnNonAccessibleMatters = useHasPermissions(['CREATE.TIMECARDS.NON_ACCESSIBLE_MATTERS']);
    // The resulting case inherits the source case's client, so which of the three create grants applies
    // depends on that client's relationship type — the backend re-checks it against the party on save.
    const canCreateMatters = useHasPermissions(['CREATE.MATTERS', 'CREATE.MATTERS.LEADS', 'CREATE.MATTERS.CLIENTS']);
    const { addAndStartTimecard, isSubmitting: isStartingTimecard } = useAddAndStartTimecard(matter);
    const isOfferFlowEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFER_FLOW);
    const reopenMenuItem = useReopenAsOfferPending(matter);
    const closeCaseMenuItem = useCloseCaseMenuItem(matter);

    // Once the user has already started tracking this case, hide the start-tracking button (not just
    // disable) to prevent duplicate drafts. Fail closed: keep it hidden until the check has loaded,
    // so a click during the initial load can't slip past and create a second draft.
    const { user } = useAuth();
    const { data: ownDraftTimecards, isLoading: isLoadingOwnDrafts } = useGetPaginatedTimecardsWithFiltersQuery(
        {
            page: 0,
            pageSize: 1,
            filters: `createdByEmployee.id==(${user?.id});timecardStatusTypeCode=in=(DRAFT);matter.id==(${matter.id})`,
        },
        { skip: !user?.id }
    );
    const hasOwnDraftTimecard = isLoadingOwnDrafts || (ownDraftTimecards?.totalElements ?? 0) > 0;

    // Check for overdue invoices
    const currentDate = new Date().toISOString().split('T')[0];
    const { data: overdueInvoices } = useGetInvoicesWithFiltersQuery({
        filters: `invoiceTypeCode=in=(DEBIT,PREPAYMENT);dueDate=duedateispast=${currentDate};invoiceStatusTypeCode=in=(SENT);id=ismatterinvoice=${matter.id}`,
        page: 0,
        pageSize: 1,
    });
    const { matterStateTypeCode } = matter;
    const isCaseClosed =
        matterStateTypeCode === MatterStateType.COMPLETED ||
        matterStateTypeCode === MatterStateType.OFFER_LOST ||
        matterStateTypeCode === MatterStateType.OFFER_WON;

    const isClientPersonaNonGrata = matter.clientParty?.isPersonaNonGrata === true;
    const addTimecardDisabledTooltipText = isClientPersonaNonGrata
        ? t('contacts.tooltips.add-new-case-disabled-persona-non-grata')
        : undefined;

    const statusChipType = useMemo<ChipType>(() => {
        switch (matter.matterStateTypeCode) {
            case 'ARCHIVED':
                return 'outlined';
            case 'COMPLETED':
                return 'neutral';
            case 'IN_PROGRESS':
                return 'success';
            case 'OFFER_LOST':
                return 'inactive';
            case 'OFFER_PENDING':
                return 'warning';
            case 'OFFER_WON':
                return 'finalized';
        }
    }, [matter.matterStateTypeCode]);

    const deleteMatterHandler = async () => {
        if (!matter.id || isNaN(Number(matter.id))) {
            return;
        }
        let validation: MatterDeleteValidation;
        try {
            validation = await fetchDeleteValidation({ id: `${matter.id}` }).unwrap();
        } catch (e) {
            console.error('matter delete-validation fetch failed: ', e);
            errorAlert(t('matters.error-checking-deletability'));
            return;
        }
        const blockerMessage = buildDeleteBlockerMessage(validation);
        if (blockerMessage) {
            warningAlert(blockerMessage);
            return;
        }
        openModal(
            <ConfirmationModal
                onCancel={closeModal}
                onConfirm={() => {
                    void deleteMatter({ id: `${matter.id}` })
                        .unwrap()
                        .then(() => {
                            leaveDeleted();
                            closeModal();
                        })
                        .catch((e) => {
                            console.error(e);
                            errorAlert(t('forms.error-submitting-the-form'));
                        });
                }}
                content={<p className="text-body1">{t('matters.alerts.delete-confirmation-text')}</p>}
            />
        );
    };

    const openNewConnectedCaseHandler = () => {
        const prefill: MatterAddPrefill = { originatesFromMatterId: matter.id };
        navigateWithReturn('/matter/new', { state: { prefill } });
    };

    // An Offer Won case can only ever produce one resulting case, so the action goes away once it exists.
    const canOpenNewConnectedCase =
        isOfferFlowEnabled &&
        matterStateTypeCode === MatterStateType.OFFER_WON &&
        !matter.connectedCase &&
        !matter.isRestricted &&
        canCreateMatters;

    const menuItems: MenuItem[] = [];
    if (closeCaseMenuItem) {
        menuItems.push(closeCaseMenuItem);
    }
    if (canOpenNewConnectedCase) {
        menuItems.push({
            handler: openNewConnectedCaseHandler,
            title: t('matters.open-new-connected-case'),
        });
    }
    if (isOfferFlowEnabled && reopenMenuItem) {
        menuItems.push(reopenMenuItem);
    }
    if (matter.isDeletable) {
        menuItems.push({
            handler: () => {
                void deleteMatterHandler();
            },
            title: t('matters.delete'),
            variant: 'destructive',
        });
    }

    if (deleteMatterLoading) {
        return <Loader />;
    }

    const hasOverdueInvoices = overdueInvoices && overdueInvoices.totalElements > 0;

    return (
        <PageHeader
            title={matter.fullName}
            iconsEnd={matter.fullName ? <CopyToClipboardIcon text={matter.fullName} className="ml-0" /> : null}
            icons={
                <>
                    {hasOverdueInvoices && (
                        <OverdueInvoices
                            className="h-[36px] w-[36px] text-[35px]"
                            tooltipText="matters.has-overdue-invoices"
                        />
                    )}
                    {matter.isCurrentlyBlocked && !matter.isRestricted && (
                        <Blocked
                            className="h-[36px] w-[36px] text-[35px]"
                            tooltipText={'dashboard.tooltips.timecard-blocked'}
                        />
                    )}
                </>
            }
            showBreadcrumb
            onBack={goBack}
            buttons={
                <div className="flex flex-row justify-end gap-2">
                    <KebabMenu
                        menuItems={menuItems}
                        icon={<MoreVertOutlinedIcon fontSize="medium" />}
                        className="h-10"
                    />
                    <PermissionButton
                        hasAuthority={matter.isEditable}
                        variant="outlined"
                        size="small"
                        onClick={() => navigateWithReturn(`/matter/${matter.id}/edit`)}
                    >
                        <FormattedMessage id="edit" />
                    </PermissionButton>
                    {!matter.isRestricted && (
                        <PermissionButton
                            permissions={['READ.MATTERS.AUDIT_TRAIL']}
                            variant="outlined"
                            color="secondary"
                            size="small"
                            onClick={onOpenHistory}
                            aria-label={t('audit-trail.matter.button')}
                            title={t('audit-trail.matter.button')}
                            className="aspect-square min-w-0 px-0!"
                        >
                            <HistoryRoundedIcon fontSize="small" />
                        </PermissionButton>
                    )}
                    {!hasOwnDraftTimecard && (!matter.isRestricted || canCreateTimecardsOnNonAccessibleMatters) && (
                        <CallForActionButtonTooltip title={addTimecardDisabledTooltipText}>
                            <span>
                                <PermissionButton
                                    permissions={['CREATE.TIMECARDS']}
                                    variant="outlined"
                                    size="small"
                                    // Same treatment as the sidebar tracker button, so the two entry points to
                                    // tracking read as one action and this one no longer twins "Add new timecard".
                                    // Outlined carries the white fill and the border that keeps it off a white header.
                                    className="group hover:border-info! hover:bg-info! hover:text-primary-foreground!"
                                    startIcon={
                                        <PlayCircleIcon className="text-info group-disabled:text-fg-disabled group-hover:text-primary-foreground size-5" />
                                    }
                                    onClick={() => {
                                        void addAndStartTimecard();
                                    }}
                                    disabled={
                                        matter.isCurrentlyBlocked ||
                                        isCaseClosed ||
                                        isClientPersonaNonGrata ||
                                        isStartingTimecard
                                    }
                                >
                                    <FormattedMessage id="timecards.start-tracking" />
                                </PermissionButton>
                            </span>
                        </CallForActionButtonTooltip>
                    )}
                    {(!matter.isRestricted || canCreateTimecardsOnNonAccessibleMatters) && (
                        <CallForActionButtonTooltip title={addTimecardDisabledTooltipText}>
                            <div>
                                <PermissionButton
                                    permissions={['CREATE.TIMECARDS']}
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    onClick={() => navigateWithReturn(`/matter/${matter.id}/timecards/new`)}
                                    disabled={matter.isCurrentlyBlocked || isCaseClosed || isClientPersonaNonGrata}
                                >
                                    <FormattedMessage id="timecards.add-new-timecard" />
                                </PermissionButton>
                            </div>
                        </CallForActionButtonTooltip>
                    )}
                </div>
            }
        >
            <div className="flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2">
                <div className={SECTION_CLASS}>
                    <MatterIndicators
                        isConfidential={matter.isConfidential}
                        hasAccess={!matter.isRestricted}
                        variant="chip"
                    />
                    {matter.isRestricted ? null : (
                        <>
                            {matter.isNonBillable ? (
                                <Chip
                                    className="mt-1"
                                    size="small"
                                    label={<MoneyOffRoundedIcon />}
                                    type={'partyType'}
                                />
                            ) : null}
                            <StatusChip
                                type={statusChipType}
                                label={getClassifierText(classifiers['MATTER_STATE_TYPE'], matter.matterStateTypeCode)}
                            />
                            <DisplayHistoryButton
                                entityType={HistoryEntityType.MATTER}
                                entityId={matter.id.toString()}
                                fieldType={HistoryFieldType.MATTER_STATE_TYPE_CODE}
                                fieldName={t('matters.state')}
                                activity={t('updating')}
                            />
                        </>
                    )}
                </div>
                <div className={SECTION_CLASS}>
                    <FormattedMessage id="matters.case-number" />
                    :&nbsp;
                    <EditableMatterCaseNumber matterId={matter.id} caseNumber={matter.caseNumber} />
                </div>
                {getStyledLink(matter?.clientParty, 'matters.client', 'contact')}
                {getStyledLinkWithHistory(
                    matter?.responsibleEmployee,
                    matter?.id,
                    'matters.responsible-lawyer',
                    'employee',
                    HistoryFieldType.RESPONSIBLE_EMPLOYEE,
                    t('matters.responsible-lawyer')
                )}
                {getStyledLinkWithHistory(
                    matter?.supervisedByEmployee,
                    matter?.id,
                    'matters.supervising-partner',
                    'employee',
                    HistoryFieldType.SUPERVISING_EMPLOYEE,
                    t('matters.supervised-by')
                )}
                {matter.isAmlApplicable && (
                    <Tooltip content={t('matters.aml-applicable-tooltip')}>
                        <span>
                            <StatusChip type={'warning'} label={t('matters.aml-applicable')} />
                        </span>
                    </Tooltip>
                )}
                {getStyledLink(matter?.primaryContactParty, 'matters.primary-contact', 'contact')}
                {matter?.dmsLink ? <DmsLink link={matter?.dmsLink} isIconDisplayed={true} /> : null}
            </div>
        </PageHeader>
    );
};

/**
 * A draft's header in classic: its title with ⋮ › Delete draft, Edit and Share as far as the reader's right on the
 * draft goes, then the Draft and state chips, the case's people and who created it. Time entry and history wait for
 * the case to be created.
 */
export const MatterDraftPageHeader = ({
    matter,
    classifiers,
    actions,
}: Omit<Props, 'onOpenHistory'> & { actions: DraftActions }): JSX.Element => {
    const goBack = useGoBack('/matters');

    return (
        <PageHeader
            title={matter.fullName}
            iconsEnd={matter.fullName ? <CopyToClipboardIcon text={matter.fullName} className="ml-0" /> : null}
            showBreadcrumb
            onBack={goBack}
            buttons={
                <div className="flex flex-row justify-end gap-2">
                    <DraftRecordActions actions={actions} />
                </div>
            }
        >
            <div className="flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2">
                <div className={SECTION_CLASS}>
                    <DraftChip />
                    <StatusChip
                        type={getStatusChipType(matter.matterStateTypeCode)}
                        label={getClassifierText(classifiers['MATTER_STATE_TYPE'], matter.matterStateTypeCode)}
                    />
                </div>
                {getStyledLink(matter.clientParty, 'matters.client', 'contact')}
                {getStyledLinkWithHistory(
                    matter.responsibleEmployee,
                    matter.id,
                    'matters.responsible-lawyer',
                    'employee'
                )}
                {getStyledLinkWithHistory(
                    matter.supervisedByEmployee,
                    matter.id,
                    'matters.supervising-partner',
                    'employee'
                )}
                {actions.draft ? <DraftMeta draft={actions.draft} /> : null}
            </div>
        </PageHeader>
    );
};

const getStyledLink = (party: (PartyNameParty & { id?: number }) | undefined | null, labelId: string, link: string) => {
    if (!party?.id) return null;
    return (
        <div className={SECTION_CLASS}>
            <FormattedMessage id={labelId} />
            :&nbsp;
            <RouterLink className={HEADER_LINK_CLASS} to={`/${link}/${party.id}`}>
                <PartyName party={party} />
            </RouterLink>
        </div>
    );
};

const getStyledLinkWithHistory = (
    entity: (NamedEntityParts & { id?: number }) | undefined | null,
    matterId: number | undefined,
    labelId: string,
    link: string,
    historyFieldType?: HistoryFieldType,
    historyFieldName?: string
) => {
    if (!entity?.id || !matterId) return null;
    return (
        <div className={SECTION_CLASS}>
            <FormattedMessage id={labelId} />
            :&nbsp;
            <RouterLink className={HEADER_LINK_CLASS} to={`/${link}/${entity.id}`}>
                <EmployeeName entity={entity} />
            </RouterLink>
            {historyFieldType ? (
                <DisplayHistoryButton
                    entityType={HistoryEntityType.MATTER}
                    entityId={matterId.toString()}
                    fieldType={historyFieldType}
                    fieldName={historyFieldName ?? ''}
                    activity={t('updating')}
                />
            ) : null}
        </div>
    );
};
